/**
 * Shared contract between the API and the web client.
 * Money is ALWAYS integer minor units (paise). Never floats.
 */

// Field rules shared by the apps and the API (phone, email, password, address…).
export * from './validation.js';

export const SUBSCRIPTION_STATUS = ['trialing', 'active', 'paused', 'past_due', 'cancelled'];
export const ORDER_STATUS = ['scheduled', 'packing', 'out_for_delivery', 'delivered', 'failed', 'skipped', 'cancelled'];
export const ORDER_TYPE = ['subscription', 'one_off', 'sample'];
export const PLAN_INTERVAL = ['daily', 'weekly', 'monthly'];
export const PRODUCT_KIND = ['box', 'addon', 'sample_box'];

/**
 * Pricing is ALL-IN per box and flat across every delivery zone: the figure a
 * customer sees is the figure they pay. Delivery is included rather than added
 * at checkout, so there is no surprise line item on the confirmation screen.
 *
 * The sample box is the one exception. The box itself is free, and the ₹29 is
 * an explicit handling & delivery charge — so it lives in `delivery_fee`, not
 * in a price. That keeps "we gave away a free box and recovered ₹29 of cost"
 * legible in the revenue reporting instead of looking like a ₹29 sale.
 */

/** Sample box: free box, customer pays handling & delivery only. Once per customer. */
export const SAMPLE_BOX_DELIVERY_FEE = 2900; // ₹29.00

/** Standard box bought once, without a subscription. Delivery included. */
export const ONE_OFF_BOX_PRICE = 8900; // ₹89.00

/** Standard box received on a subscription. Delivery included. */
export const SUBSCRIPTION_BOX_PRICE = 7500; // ₹75.00

// Delivery is baked into the prices above, so nothing is added at checkout.
export const ONE_OFF_DELIVERY_FEE = 0;
export const SUBSCRIPTION_DELIVERY_FEE = 0;

/** What a customer saves per box by subscribing rather than buying one-off. */
export const SUBSCRIPTION_SAVING_PER_BOX = ONE_OFF_BOX_PRICE - SUBSCRIPTION_BOX_PRICE; // ₹14.00

/**
 * The price to charge for a product on a given channel.
 * `subscription_price` is optional per product; absent means no discount.
 */
export function priceFor(product, channel = 'one_off') {
  if (channel === 'subscription') {
    return product.subscription_price ?? product.subscriptionPrice ?? product.price;
  }
  return product.price;
}

export function formatMoney(minorUnits, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format((minorUnits ?? 0) / 100);
}

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Subscription pricing is PER BOX, not a fixed monthly figure — the monthly
 * cost depends on how many delivery days the plan has. A month averages ~4.345
 * weeks, so a weekdays plan (5 days) is ~22 boxes, all-days ~30.
 */
export const WEEKS_PER_MONTH = 4.345;

/** How many boxes a plan delivers in a typical month, from its weekdays. */
export function deliveriesPerMonth(weekdays) {
  return Math.round((weekdays?.length ?? 0) * WEEKS_PER_MONTH);
}

/** Estimated monthly cost (paise) = boxes this month × per-box price. */
export function monthlyEstimate(weekdays, perBoxPaise) {
  return deliveriesPerMonth(weekdays) * (perBoxPaise ?? 0);
}

/** Boxes delivered in a typical week = number of delivery weekdays. */
export function deliveriesPerWeek(weekdays) {
  return weekdays?.length ?? 0;
}

/** Estimated weekly cost (paise) = boxes this week × per-box price. */
export function weeklyEstimate(weekdays, perBoxPaise) {
  return deliveriesPerWeek(weekdays) * (perBoxPaise ?? 0);
}

/**
 * How a subscription is billed. `weekly` is the default — the customer is
 * charged each week for that week's boxes at the standard per-box rate.
 * `monthly_advance` charges a whole month upfront at a discounted per-box rate
 * (₹5/box off), and is offered on EVERY plan, custom included.
 */
export const BILLING_MODE = ['weekly', 'monthly_advance'];

/**
 * API surface, kept in one place so the client and server can't drift.
 */
export const API = {
  auth: {
    register: 'POST /api/auth/register',
    login: 'POST /api/auth/login',
    refresh: 'POST /api/auth/refresh',
    logout: 'POST /api/auth/logout',
    me: 'GET /api/auth/me',
  },
  // Everything under /api/me is caller-scoped: the user id comes from the JWT,
  // never from the path, so there is no id to tamper with.
  me: {
    updateProfile: 'PATCH /api/me',
    changePassword: 'POST /api/me/password',
    wallet: 'GET /api/me/wallet',
    addresses: 'GET /api/me/addresses',
    createAddress: 'POST /api/me/addresses',
    updateAddress: 'PATCH /api/me/addresses/:id',
    deleteAddress: 'DELETE /api/me/addresses/:id',
    paymentMethods: 'GET /api/me/payment-methods',
    addPaymentMethod: 'POST /api/me/payment-methods',
    removePaymentMethod: 'DELETE /api/me/payment-methods/:id',
    // Register this device's push token (app calls after permission granted).
    deviceToken: 'POST /api/me/device-token',
  },
  catalog: {
    // { plans:[{...,is_custom}], monthlyPrepayDiscountPercent }
    plans: 'GET /api/catalog/plans',
    products: 'GET /api/catalog/products',
    // Delivery time windows (morning + hourly slots) for the pickers.
    slots: 'GET /api/catalog/slots',
    // { monthlyPrepayDiscountPercent, maxSkipsPerWeek, oneOffDeliveryFee, sampleBoxFee }
    config: 'GET /api/catalog/config',
    // Pictorial "what's in the box" for a day. Defaults to TOMORROW.
    // { date, items:[{ id, name, benefit, emoji, imageUrl, accent }] }
    dailyMenu: 'GET /api/catalog/daily-menu?date=YYYY-MM-DD',
  },
  subscriptions: {
    list: 'GET /api/subscriptions',
    get: 'GET /api/subscriptions/:id',
    create: 'POST /api/subscriptions',
    pause: 'POST /api/subscriptions/:id/pause',
    resume: 'POST /api/subscriptions/:id/resume',
    cancel: 'DELETE /api/subscriptions/:id',
    updateSchedule: 'PATCH /api/subscriptions/:id/schedule',
    updateItems: 'PUT /api/subscriptions/:id/items',
    upcoming: 'GET /api/subscriptions/:id/upcoming',
    skipDate: 'POST /api/subscriptions/:id/skip',
    // Day-by-day view over a date range; each day carries its effective box.
    calendar: 'GET /api/subscriptions/:id/calendar?from=&to=',
    // Replace one day's box in full; empty items array reverts to the default.
    setDayBox: 'PUT /api/subscriptions/:id/day/:date/items',
  },
  orders: {
    list: 'GET /api/orders',
    get: 'GET /api/orders/:id',
    oneOff: 'POST /api/orders/one-off',
    sample: 'POST /api/orders/sample',
    invoice: 'GET /api/orders/:id/invoice',
  },
  // Paytm Payment Gateway (India, incl. UPI). initiate returns either
  // { mock: true, amount } (no keys) or { txnToken, gatewayOrderId, mid,
  //  amount, checkoutJsUrl, isProd } to open Paytm's checkout JS.
  payments: {
    paytmInitiate: 'POST /api/payments/paytm/initiate',   // { orderId }
    paytmStatus: 'GET /api/payments/paytm/status/:gatewayOrderId',
    // Paytm posts here server-side, then redirects the browser to
    // /checkout/result?ref=&status=success|failed|pending
    paytmCallback: 'POST /api/payments/paytm/callback',
  },
  // Delivery-boy (driver) facing. Role 'driver' (or admin) only.
  driver: {
    route: 'GET /api/driver/route?date=YYYY-MM-DD',       // { date, total, delivered, stops:[...] }
    updateStop: 'PATCH /api/driver/orders/:id/status',    // { status: out_for_delivery|delivered|failed }
  },
  admin: {
    analytics: 'GET /api/admin/analytics',
    packingList: 'GET /api/admin/packing-list?date=YYYY-MM-DD',
    routes: 'GET /api/admin/routes?date=YYYY-MM-DD',
    updateFulfilment: 'PATCH /api/admin/orders/:id/status',
    customers: 'GET /api/admin/customers',
    customer: 'GET /api/admin/customers/:id',
    issueCredit: 'POST /api/admin/customers/:id/credit',
    refund: 'POST /api/admin/payments/:id/refund',
    // Includes inactive products, unlike the public catalog.
    products: 'GET /api/admin/products',
    createProduct: 'POST /api/admin/products',
    updateProduct: 'PATCH /api/admin/products/:id',
    // Deactivates rather than deleting — past order line items must survive.
    deactivateProduct: 'DELETE /api/admin/products/:id',
    inventory: 'GET /api/admin/inventory?date=YYYY-MM-DD',
    // Body: { date, entries: [{ productId, capacity }] }
    setInventory: 'PUT /api/admin/inventory',
    // Subscription plans (delivery cadences) — editable like products.
    plans: 'GET /api/admin/plans',
    createPlan: 'POST /api/admin/plans',        // { name, price, deliveryWeekdays, isCustom?, isActive? }
    updatePlan: 'PATCH /api/admin/plans/:id',
    deactivatePlan: 'DELETE /api/admin/plans/:id',
    // Daily menu — the pictorial fruit/veg library and per-date combinations.
    menuItems: 'GET /api/admin/menu-items',
    createMenuItem: 'POST /api/admin/menu-items',      // { name, benefit, emoji, accent?, imageUrl?, sortOrder?, isActive? }
    updateMenuItem: 'PATCH /api/admin/menu-items/:id',
    deactivateMenuItem: 'DELETE /api/admin/menu-items/:id',
    dailyMenu: 'GET /api/admin/daily-menu?date=YYYY-MM-DD',
    setDailyMenu: 'PUT /api/admin/daily-menu',         // { date, itemIds:[...] }
    // Delivery time slots — editable like products.
    slots: 'GET /api/admin/slots',
    createSlot: 'POST /api/admin/slots',
    updateSlot: 'PATCH /api/admin/slots/:id',
    deactivateSlot: 'DELETE /api/admin/slots/:id',
    // Admin-editable global pricing. Per-box prices live on products instead.
    settings: 'GET /api/admin/settings',
    updateSettings: 'PUT /api/admin/settings',
    settingsHistory: 'GET /api/admin/settings/history?key=',
    tickets: 'GET /api/admin/tickets',
    ticket: 'GET /api/admin/tickets/:id',
    updateTicket: 'PATCH /api/admin/tickets/:id',
    replyToTicket: 'POST /api/admin/tickets/:id/messages',
    // Delivery boys (drivers) & assignment.
    drivers: 'GET /api/admin/drivers?date=YYYY-MM-DD',
    createDriver: 'POST /api/admin/drivers',              // { fullName, email, phone?, password }
    updateDriver: 'PATCH /api/admin/drivers/:id',         // { isActive }
    autoAssign: 'POST /api/admin/routes/auto-assign',     // { date, reassign? }
    assignRoute: 'POST /api/admin/routes/assign',         // { date, driverId, orderIds:[...] }
  },
};
